import { APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseFearAndGreed {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any[] = [];
  private currentDataResponse: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} Fear and Greed Index</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}</div>
        </div>
        <div class="inBody">
          <div class="optionsRow" data-testing-id="options-row">
            <div class="item dropStyle01">
              
            </div>
          </div>

          <div class="chartDv fearAndGreedMain" data-testing-id="fear-greed-main">
            <div class="chart" id="fearGreedGauge" style="width: 100%; height: 150px;" data-testing-id="fear-greed-gauge"></div>
            <div class="cryptoCard" data-type="historical" data-testing-id="historical-card"></div>
            <div class="cryptoCard" data-type="yearly" data-testing-id="yearly-card"></div>
          </div>

        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const res = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/crypto/fear_greed_index?interval=30d`,
        body: { interval: '30d' },
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) throw new Error("Failed to fetch Fear & Greed Index data");

      const json = await res.json();
      this.currentDataResponse = json;
      this.currentData = json.data?.all
        ?.filter((d: any) => d.data_type === "fear_and_greed")
        ?.sort((a: any, b: any) => b.timestamp_unix - a.timestamp_unix) || [];

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private updateDisplay(): void {
    const mainEl = this.shadowRoot.querySelector('.fearAndGreedMain') as HTMLElement;
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData) || !this.currentDataResponse?.data) {
      if (mainEl) mainEl.innerHTML = getNoDataHTML();
      return;
    }
    if (mainEl && !mainEl.querySelector('#fearGreedGauge')) {
      mainEl.innerHTML = `
        <div class="chart" id="fearGreedGauge" style="width: 100%; height: 150px;" data-testing-id="fear-greed-gauge"></div>
        <div class="cryptoCard" data-type="historical" data-testing-id="historical-card"></div>
        <div class="cryptoCard" data-type="yearly" data-testing-id="yearly-card"></div>
      `;
    }
    if (mainEl) {
      mainEl.setAttribute('data-testing-id', 'fear-greed-main');
    }

    const json = this.currentDataResponse;

    const { current, yesterday, lastWeek, lastMonth, yearlyHigh, yearlyLow } = this.getDisplayData(json.data);

    // Update gauge
    const rawGaugeValue = Number(current?.value ?? 0);
    const gaugeValue = Number.isFinite(rawGaugeValue) ? rawGaugeValue : 0;
    const classification = current?.value_classification ?? 'N/A';
    this.renderFearGreedGaugeChart(gaugeValue, classification);

    // Historical section
    const histContainer = this.shadowRoot.querySelector('.cryptoCard[data-type="historical"]');
    if (histContainer) {
      histContainer.innerHTML = `
        <h2 data-testing-id="historical-title">Historical Values</h2>
        <div class="valueWrapper" data-testing-id="historical-yesterday" data-period="yesterday">
          <p data-testing-id="period-label">Yesterday</p>
          <span class="expand"></span>
          <div class="tokenDv neutral01" data-testing-id="period-value" data-value="${yesterday?.value ?? '-'}" data-classification="${yesterday?.value_classification ?? 'N/A'}"><span>${yesterday?.value_classification ?? 'N/A'} - ${yesterday?.value ?? '-'}</span></div>
        </div>
        <div class="valueWrapper" data-testing-id="historical-last-week" data-period="lastWeek">
          <p data-testing-id="period-label">Last Week</p>
          <span class="expand"></span>
          <div class="tokenDv neutral02" data-testing-id="period-value" data-value="${lastWeek?.value ?? '-'}" data-classification="${lastWeek?.value_classification ?? 'N/A'}"><span>${lastWeek?.value_classification ?? 'N/A'} - ${lastWeek?.value ?? '-'}</span></div>
        </div>
        <div class="valueWrapper" data-testing-id="historical-last-month" data-period="lastMonth">
          <p data-testing-id="period-label">Last Month</p>
          <span class="expand"></span>
          <div class="tokenDv neutral03" data-testing-id="period-value" data-value="${lastMonth?.value ?? '-'}" data-classification="${lastMonth?.value_classification ?? 'N/A'}"><span>${lastMonth?.value_classification ?? 'N/A'} - ${lastMonth?.value ?? '-'}</span></div>
        </div>
      `;
    }

    // Yearly section
    const yearContainer = this.shadowRoot.querySelector('.cryptoCard[data-type="yearly"]');
    if (yearContainer) {
      const highDate = yearlyHigh?.timestamp ? new Date(yearlyHigh.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
      const lowDate = yearlyLow?.timestamp ? new Date(yearlyLow.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

      yearContainer.innerHTML = `
        <h2 data-testing-id="yearly-title">Yearly High and Low</h2>
        <div class="valueWrapper" data-testing-id="yearly-high" data-type="high">
          <p data-testing-id="yearly-date">Yearly High (${highDate})</p>
          <span class="expand"></span>
          <div class="tokenDv green" data-testing-id="yearly-value" data-value="${yearlyHigh?.value ?? '-'}" data-classification="${yearlyHigh?.value_classification ?? 'N/A'}"><span>${yearlyHigh?.value_classification ?? 'N/A'} - ${yearlyHigh?.value ?? '-'}</span></div>
        </div>
        <div class="valueWrapper" data-testing-id="yearly-low" data-type="low">
          <p data-testing-id="yearly-date">Yearly Low (${lowDate})</p>
          <span class="expand"></span>
          <div class="tokenDv red" data-testing-id="yearly-value" data-value="${yearlyLow?.value ?? '-'}" data-classification="${yearlyLow?.value_classification ?? 'N/A'}"><span>${yearlyLow?.value_classification ?? 'N/A'} - ${yearlyLow?.value ?? '-'}</span></div>
        </div>
      `;
    }
  }

  private getDisplayData(data: any): any {
    const all = (data?.all || [])
      .filter((d: any) => d?.data_type === "fear_and_greed")
      .sort((a: any, b: any) => (b.timestamp_unix ?? 0) - (a.timestamp_unix ?? 0));
    const shouldUseSeries = !data?.current && !data?.yesterday && all.length;

    return {
      current: data?.current ?? all[0],
      yesterday: data?.yesterday ?? all[1],
      lastWeek: shouldUseSeries ? this.getSeriesPointByDays(all, 7) : (data?.lastWeek ?? this.getSeriesPointByDays(all, 7)),
      lastMonth: shouldUseSeries ? this.getSeriesPointByDays(all, 30) : (data?.lastMonth ?? this.getSeriesPointByDays(all, 30)),
      yearlyHigh: data?.yearlyHigh ?? this.getSeriesExtreme(all, 'high'),
      yearlyLow: data?.yearlyLow ?? this.getSeriesExtreme(all, 'low')
    };
  }

  private getSeriesPointByDays(series: any[], daysBack: number): any {
    if (!series.length) return null;
    const latest = series[0]?.timestamp_unix;
    if (typeof latest !== 'number') return series[Math.min(daysBack, series.length - 1)];

    const target = latest - (daysBack * 24 * 60 * 60);
    return series.reduce((closest, item) => {
      const itemTime = item?.timestamp_unix;
      const closestTime = closest?.timestamp_unix;
      if (typeof itemTime !== 'number') return closest;
      if (typeof closestTime !== 'number') return item;
      return Math.abs(itemTime - target) < Math.abs(closestTime - target) ? item : closest;
    }, series[0]);
  }

  private getSeriesExtreme(series: any[], type: 'high' | 'low'): any {
    if (!series.length) return null;
    return series.reduce((selected, item) => {
      const selectedValue = Number(selected?.value);
      const itemValue = Number(item?.value);
      if (!Number.isFinite(itemValue)) return selected;
      if (!Number.isFinite(selectedValue)) return item;
      return type === 'high'
        ? (itemValue > selectedValue ? item : selected)
        : (itemValue < selectedValue ? item : selected);
    }, series[0]);
  }

  private renderFearGreedGaugeChart(gaugeValue: number, sentimentText: string): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#fearGreedGauge') as HTMLElement;
    if (!container) return;

    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    const chart = echarts.init(container);
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--text-white');

    const option = {
      series: [{
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: 100,
        center: ['50%', '58%'],
        radius: '100%',
        axisLine: {
          lineStyle: {
            width: 10,
            color: [
              [0.2, '#FF3B3B'],
              [0.4, '#FF895A'],
              [0.6, '#CBFF18'],
              [0.8, '#34E36F'],
              [1, '#009E3A']
            ]
          }
        },
        pointer: { show: false },
        axisLabel: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        progress: {
          show: true,
          roundCap: false,
          width: 10,
          itemStyle: {
            color:
              gaugeValue <= 20 ? '#FF3B3B' :
                gaugeValue <= 40 ? '#FF895A' :
                  gaugeValue <= 60 ? '#CBFF18' :
                    gaugeValue <= 80 ? '#34E36F' : '#009E3A'
          },
        },
        detail: {
          offsetCenter: [0, '-10%'],
          formatter: `{value|${gaugeValue}}\n{sentiment|${sentimentText}}`,
          rich: {
            value: { fontSize: 40, fontWeight: 'bold', color: textColor },
            sentiment: { fontSize: 18, color: textColor }
          }
        },
        data: [{ value: gaugeValue }]
      }]
    };

    chart.setOption(option);
    
    // Add data-testing-id to chart after render
    setTimeout(() => {
      const chartSvg = this.shadowRoot.querySelector('#fearGreedGauge svg');
      if (chartSvg) {
        chartSvg.setAttribute('data-testing-id', 'fear-greed-gauge-svg');
      }
    }, 500);
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (stockId) => this.setupChart(stockId),
      (type, data) => this.emit(type, data)
    );
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    this.widgetResizeObserver?.disconnect();
    if (this.updateInterval) clearInterval(this.updateInterval);
    this.container.innerHTML = '';
    this.eventHandlers.clear();
  }

  private emit(type: WidgetEventType, data: any): void {
    this.eventHandlers.get(type)?.forEach(handler => handler({ type, data }));
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window { TraderverseFearAndGreed: typeof TraderverseFearAndGreed; }
}
if (typeof window !== 'undefined') {
  window.TraderverseFearAndGreed = TraderverseFearAndGreed;
}